<template>
  <footer class="footer">
    <div class="container">
      <div class="footer-content">
        <div class="footer-top">
          <!-- 品牌区域 -->
          <div class="footer-brand">
            <router-link to="/" class="brand-link">
              <img :src="logo" alt="比优集团" class="footer-logo" @click="toHome">
              <div class="brand-text"></div>
            </router-link>
            <!-- 补充：社交媒体图标展示 -->
            <div class="social-icons">
              <a v-if="socialLinks.weChat" :href="socialLinks.weChat" class="social-icon" target="_blank" rel="noopener">
                <ChatRound />
              </a>
              <a v-if="socialLinks.linkedIn" :href="socialLinks.linkedIn" class="social-icon" target="_blank" rel="noopener">
                <Share />
              </a>
            </div>
          </div>

          <!-- ✅ 核心优化：动态渲染导航列表 替代写死的导航 -->
          <div class="footer-nav">
            <template
              v-for="menu in menuData"
              :key="menu.path">

              <div
                class="nav-col"
                v-if="menu.path !== '/'"
              >
                <h4 class="nav-title">{{ t(menu.i18nKey) }}</h4>
                <!-- 第一处：子菜单的 router-link 替换成这个 -->
                <a
                  v-for="child in menu.children"
                  :key="child.path"
                  class="nav-link"
                  @click="handleNavClick(child.path)"
                >
                  {{ t(child.i18nKey) }}
                </a>

                <!-- 第二处：无自菜单的项的 router-link 替换成这个 -->
                <a
                  v-if="!menu.children || menu.children.length === 0"
                  :key="menu.path"
                  class="nav-link"
                  @click="handleNavClick(menu.path)"
                >
                  {{ t(menu.i18nKey) }}
                </a>
              </div>
            </template>
          </div>
        </div>

        <!-- 版权区域 -->
        <div class="footer-copyright">
          <div class="copyright-info">
            <p class="copyright-text">{{ t('footer.copyright', { year: currentYear }) }}</p>
            <p class="company-info">{{ t('footer.company_info') }}</p>
          </div>
<!--          <div class="footer-legal">-->
<!--            <a href="/legal" class="legal-link">{{ t('footer.legal') }}</a>-->
<!--            <span class="divider">|</span>-->
<!--            <a href="/privacy" class="legal-link">{{ t('footer.privacy') }}</a>-->
<!--          </div>-->
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChatRound, Share } from '@element-plus/icons-vue';
import logo from '@/assets/img/logo.png';
import logoF from '@/assets/img/logoF.png';
// 核心导入：引入外部的menuData，替换写死的导航数据
import { menuData } from '@/mock/data'; // 请根据你的menuData实际存放路径修改！！！
import { useRouter } from 'vue-router';
const { t } = useI18n();
const router = useRouter();
// 社交媒体链接（可根据实际情况配置）
const socialLinks = ref({
  weChat: '',
  linkedIn: ''
});
const handleNavClick = (path: string) => {
  // 1. 清除指定的两个sessionStorage缓存项
  sessionStorage.removeItem('news_list_state');
  sessionStorage.removeItem('notice_list_state');
  // 2. 手动跳转到目标路由
  router.push(path);
};
const toHome = () => {
  router.push('/');
};
// 当前年份
const currentYear = new Date().getFullYear();
</script>

<style scoped lang="scss">
// 引入比优集团VIS统一变量
@import '@/assets/css/common';

.footer {
  background: linear-gradient(135deg, $gray-80 0%, $primary-dark-4 100%);
  color: $white;
  padding: 60px 0 20px;
  font-family: $font-base;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba($primary-color, 0.3), transparent);
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
}

.footer-content {
  display: flex;
  flex-direction: column;
  gap: 50px;
}

.footer-top {
  display: grid;
  grid-template-columns: 1fr 4fr;
  gap: 60px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
}

// 品牌区域
.footer-brand {
  .brand-link {
    display: flex;
    align-items: center;
    gap: 16px;
    text-decoration: none;
    color: inherit;
    margin-bottom: 24px;

    @media (max-width: 768px) {
      flex-direction: column;
      text-align: center;
      gap: 12px;
    }
  }

  .footer-logo {
    height: 50px;
    width: auto;

    @media (max-width: 768px) {
      height: 40px;
    }
  }
  .footer-logoF {
    height: 40px;
    width: auto;

    @media (max-width: 768px) {
      height: 32px;
    }
  }
  .brand-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .brand-title {
    font-family: $font-serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: $white;
    margin: 0;
    line-height: 1.2;

    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }

  .brand-slogan {
    font-size: 0.875rem;
    color: $primary-light-3;
    margin: 0;
    line-height: 1.4;
  }

  .social-icons {
    display: flex;
    gap: 12px;
    margin-top: 16px;

    .social-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      background: rgba($primary-color, 0.2);
      border-radius: 50%;
      color: $white;
      text-decoration: none;
      transition: all 0.3s ease;

      &:hover {
        background: $primary-color;
        transform: translateY(-2px);
      }

      :deep(.el-icon) {
        font-size: 1.125rem;
      }
    }
  }
}

// 导航区域
.footer-nav {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 30px 20px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

.nav-col {
  display: flex;
  flex-direction: column;
  gap: 16px;
  a{
    cursor: pointer;
  }
}

.nav-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: $white;
  margin: 0;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba($primary-color, 0.2);
}

.nav-link {
  color: $primary-light-3;
  text-decoration: none;
  font-size: 0.9375rem;
  transition: all 0.3s ease;
  position: relative;
  padding-left: 0;
  display: inline-block;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 6px;
    height: 6px;
    background: $primary-color;
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover,
  &.router-link-active {
    color: $white;
    padding-left: 12px;

    &::before {
      opacity: 1;
    }
  }

  &.router-link-active {
    font-weight: 500;
  }
}

// 版权区域
.footer-copyright {
  border-top: 1px solid rgba($primary-color, 0.2);
  padding-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }
}

.copyright-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.copyright-text {
  font-size: 0.875rem;
  color: $primary-light-4;
  margin: 0;
}

.company-info {
  font-size: 0.75rem;
  color: $primary-light-6;
  margin: 0;
  opacity: 0.8;
}

.footer-legal {
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    justify-content: center;
  }
}

.legal-link {
  color: $primary-light-4;
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.3s ease;

  &:hover {
    color: $white;
  }
}

.divider {
  color: $primary-light-6;
  opacity: 0.5;
}

// 响应式调整
@media (max-width: 1200px) {
  .container {
    padding: 0 30px;
  }
}

@media (max-width: 992px) {
  .footer {
    padding: 50px 0 20px;
  }

  .footer-content {
    gap: 40px;
  }

  .footer-top {
    gap: 40px;
  }

  .footer-nav {
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 0 20px;
  }

  .footer {
    padding: 40px 0 20px;
  }

  .footer-content {
    gap: 30px;
  }

  .footer-nav {
    gap: 30px 15px;
  }

  .nav-title {
    font-size: 1rem;
  }

  .nav-link {
    font-size: 0.875rem;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 16px;
  }

  .footer {
    padding: 30px 0 16px;
  }
}
</style>